import { useState } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarIcon, CheckCircle2, Calendar as CalendarIconOutline, Pause, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AutoStartAction } from '@/hooks/useAutoStartChantiers';

interface ChantierADemarrer {
  id: string;
  nom_chantier: string;
  date_debut_prevue: string;
  date_debut_reelle: string | null;
  client: string | null;
}

interface ModalDemarrageAutoProps {
  isOpen: boolean;
  chantier: ChantierADemarrer | null;
  currentIndex: number;
  totalChantiers: number;
  isProcessing: boolean;
  onAction: (action: AutoStartAction, nouvelleDate?: string) => Promise<void>;
  onSuspendre: (chantierId: string) => void;
}

/**
 * Modal de notification pour les chantiers passés automatiquement en "En cours".
 * Propose 4 options à l'utilisateur :
 * - Confirmer le démarrage
 * - Repousser la date de début
 * - Suspendre le chantier
 * - Garder en Planifié
 */
export function ModalDemarrageAuto({
  isOpen,
  chantier,
  currentIndex,
  totalChantiers,
  isProcessing,
  onAction,
  onSuspendre,
}: ModalDemarrageAutoProps) {
  useModalProtection(isOpen);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [nouvelleDate, setNouvelleDate] = useState<Date | undefined>(undefined);

  if (!chantier) return null;

  const dateDebutFormatee = format(new Date(chantier.date_debut_prevue), 'dd MMMM yyyy', { locale: fr });

  const handleRepousser = async () => {
    if (!nouvelleDate) {
      setShowDatePicker(true);
      return;
    }
    const dateStr = format(nouvelleDate, 'yyyy-MM-dd');
    await onAction('repousser', dateStr);
    setShowDatePicker(false);
    setNouvelleDate(undefined);
  };

  const handleSuspendre = () => {
    onSuspendre(chantier.id);
  };

  // Date minimum pour repousser : demain
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px]" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <span className="text-2xl">🚧</span>
              Chantier démarré automatiquement
            </DialogTitle>
            {totalChantiers > 1 && (
              <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                {currentIndex + 1}/{totalChantiers}
              </span>
            )}
          </div>
          <DialogDescription className="text-left pt-2">
            Le chantier <span className="font-semibold text-foreground">"{chantier.nom_chantier}"</span>
            {chantier.client && (
              <span className="text-muted-foreground"> ({chantier.client})</span>
            )}
            {' '}a été passé en statut <span className="font-semibold text-green-600">"En cours"</span> car
            sa date de début (<span className="font-medium">{dateDebutFormatee}</span>) est atteinte.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm font-medium mb-4">Que souhaitez-vous faire ?</p>

          <div className="space-y-3">
            {/* Option 1 : Confirmer */}
            <Button
              variant="default"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => onAction('confirmer')}
              disabled={isProcessing}
            >
              <CheckCircle2 className="h-5 w-5 mr-3 text-green-500" />
              <div className="text-left">
                <div className="font-medium">Confirmer le démarrage</div>
                <div className="text-xs text-black/70 font-normal">
                  Le chantier reste "En cours"
                </div>
              </div>
            </Button>

            {/* Option 2 : Repousser la date */}
            {!showDatePicker ? (
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-3 px-4"
                onClick={() => setShowDatePicker(true)}
                disabled={isProcessing}
              >
                <CalendarIconOutline className="h-5 w-5 mr-3 text-blue-500" />
                <div className="text-left">
                  <div className="font-medium">Repousser la date de début</div>
                  <div className="text-xs text-muted-foreground font-normal">
                    Revient en "Planifié" avec une nouvelle date
                  </div>
                </div>
              </Button>
            ) : (
              <div className="border rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium">Sélectionnez la nouvelle date de début :</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !nouvelleDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {nouvelleDate ? (
                        format(nouvelleDate, 'dd MMMM yyyy', { locale: fr })
                      ) : (
                        "Choisir une date"
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={nouvelleDate}
                      onSelect={setNouvelleDate}
                      disabled={(date) => date < minDate}
                      initialFocus
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowDatePicker(false);
                      setNouvelleDate(undefined);
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleRepousser}
                    disabled={!nouvelleDate || isProcessing}
                  >
                    Confirmer la nouvelle date
                  </Button>
                </div>
              </div>
            )}

            {/* Option 3 : Suspendre */}
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={handleSuspendre}
              disabled={isProcessing}
            >
              <Pause className="h-5 w-5 mr-3 text-orange-500" />
              <div className="text-left">
                <div className="font-medium">Suspendre le chantier</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Un imprévu empêche le démarrage
                </div>
              </div>
            </Button>

            {/* Option 4 : Garder en Planifié */}
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-3 px-4"
              onClick={() => onAction('garder_planifie')}
              disabled={isProcessing}
            >
              <ClipboardList className="h-5 w-5 mr-3 text-gray-500" />
              <div className="text-left">
                <div className="font-medium">Garder en "Planifié"</div>
                <div className="text-xs text-muted-foreground font-normal">
                  Pas prêt à démarrer (la date reste indicative)
                </div>
              </div>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
